/**
 * Reduced Motion Hook with Loading State
 *
 * Provides accessibility support with explicit "unknown" state to prevent
 * analytics and effects from firing before preference is known.
 *
 * Key Differences from useReducedMotionFallback:
 * - Returns undefined while loading (prevents race conditions)
 * - Safe default strategy: treat unknown as "reduced motion"
 * - Enables proper gating of analytics and effects
 *
 * Usage:
 * ```tsx
 * const isReducedMotion = useReducedMotionWithLoading();
 *
 * // Gate analytics until preference is known
 * useEffect(() => {
 *   if (isReducedMotion !== undefined && !isReducedMotion) {
 *     captureEvent(...);
 *   }
 * }, [isReducedMotion]);
 * ```
 *
 * @see https://docs.dramadetector.com/dev/testing/testing-patterns/#deferred-promise-pattern
 */

import { useState, useEffect } from 'react';
import { AccessibilityInfo, Platform } from 'react-native';

/**
 * Hook to detect reduced motion preference with explicit loading state.
 *
 * Returns:
 * - `undefined`: Preference not yet loaded (treat as reduced motion for safety)
 * - `true`: User has reduced motion enabled
 * - `false`: User has reduced motion disabled (animations ok)
 *
 * @returns {boolean | undefined} Reduced motion preference or undefined if loading
 */
export function useReducedMotionWithLoading(): boolean | undefined {
  const [isReducedMotion, setIsReducedMotion] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    let mounted = true;

    const checkReducedMotion = async () => {
      try {
        const enabled = await AccessibilityInfo.isReduceMotionEnabled();
        if (mounted) {
          setIsReducedMotion(enabled);
        }
      } catch (error) {
        // Web fallback: check media query
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
          if (mounted) {
            setIsReducedMotion(mediaQuery.matches);
          }
        } else {
          // Safe fallback: assume no reduced motion
          if (mounted) {
            setIsReducedMotion(false);
          }
        }
      }
    };

    checkReducedMotion();

    // Subscribe to changes
    const subscription = AccessibilityInfo.addEventListener(
      'reduceMotionChanged',
      (enabled) => {
        if (mounted) {
          setIsReducedMotion(enabled);
        }
      }
    );

    // Web: also listen to media query changes
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      const handleChange = (e: MediaQueryListEvent) => {
        if (mounted) {
          setIsReducedMotion(e.matches);
        }
      };
      mediaQuery.addEventListener('change', handleChange);

      return () => {
        mounted = false;
        subscription.remove();
        mediaQuery.removeEventListener('change', handleChange);
      };
    }

    return () => {
      mounted = false;
      subscription.remove();
    };
  }, []);

  return isReducedMotion;
}

export type UseReducedMotionWithLoadingReturn = ReturnType<typeof useReducedMotionWithLoading>;
